import { Table, Tag } from 'antd';
import moment from 'moment';
import { useMemo } from 'react';

const CommodityTable = ({ data, isDaily, currentContactStore }) => {
  const formatNumber = (number) => {
    return number
      .toFixed(2)
      .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')
      .replace('.', ',');
  };

  const getStatusTag = (status) => {
    if (!status || typeof status !== 'string') return <Tag style={{ borderRadius: '4px', border: 'none', fontWeight: 500, padding: '4px 12px' }}>НЕМАЄ ДАНИХ</Tag>;
    const statusUpper = status.toUpperCase();
    switch (statusUpper) {
      case 'ЗАТВЕРДЖЕНО':
      case 'ПЕРЕВІРЕНО':
      case 'APPROVED':
        return (
          <Tag
            style={{ borderRadius: '4px', border: 'none', fontWeight: 500, padding: '4px 12px' }}
            color="green"
          >
            ЗАТВЕРДЖЕНО
          </Tag>
        );
      case 'ПОДАНО':
      case 'SUBMITTED':
        return (
          <Tag
            style={{ borderRadius: '4px', border: 'none', fontWeight: 500, padding: '4px 12px' }}
            color="gold"
          >
            ПОДАНО
          </Tag>
        );
      case 'Є РОЗБІЖНОСТІ':
      case 'DISCREPANCY':
        return (
          <Tag
            style={{ borderRadius: '4px', border: 'none', fontWeight: 500, padding: '4px 12px' }}
            color="red"
          >
            Є РОЗБІЖНОСТІ
          </Tag>
        );
      default:
        return <Tag style={{ borderRadius: '4px', border: 'none', fontWeight: 500, padding: '4px 12px' }}>{statusUpper}</Tag>;
    }
  };
  const columns = useMemo(() => {
    const baseColumns = [
      {
        title: 'Назва',
        dataIndex: 'name',
        className: 'name center',
        width: 360 /* Increased from 270px to make it 20% wider */,
      },
      {
        title: `Сумарний товарообіг ${isDaily ? 'за добу' : 'за місяць'} (без ПДВ)`,
        dataIndex: 'amountWithoutVAT',
        className: 'excise-tax center',
        width: 250,
      },
      {
        title: 'Податок на додану вартість (ПДВ)',
        dataIndex: 'vat',
        className: 'excise-tax center',
        width: 250,
      },
      {
        title: `Сумарний товарообіг ${isDaily ? 'за добу' : 'за місяць'} (з ПДВ)`,
        dataIndex: 'amountWithVAT',
        className: 'excise-tax center',
        width: 250,
      },
      {
        title: 'Статус',
        dataIndex: 'status',
        className: 'status-col',
        align: 'center',
        width: 220,
        render: (val) => (typeof val === 'string' ? <div className="status-container">{getStatusTag(val)}</div> : val),
      },
    ];

    if (currentContactStore.cash) {
      baseColumns.splice(1, 0, {
        title: 'Готівка',
        dataIndex: 'cash',
        className: 'excise-tax center',
        width: 150,
      });
    }

    if (currentContactStore.excise) {
      baseColumns.splice(1, 0, {
        title: 'Акцизний збір ',
        dataIndex: 'exciseTax',
        className: 'excise-tax center',
        width: 250,
      });
    }

    return baseColumns;
  }, [isDaily, currentContactStore]);
  const prepareTableData = useMemo(() => {
    if (!data || !data.length) {
      return [];
    }

    // Группируем данные по дате
    const groupedByDate = {};

    // Сначала группируем данные по дате для определения уникальных дат
    data.forEach((item) => {
      if (!item || !item.date) return;

      const dateKey = moment(item.date).format('DD.MM.YYYY');
      if (!groupedByDate[dateKey]) {
        groupedByDate[dateKey] = {
          date: item.date,
          reports: [],
        };
      }
      groupedByDate[dateKey].reports.push(item);
    });

    // Создаем результирующий список строк для таблицы
    const result = [];

    // Обрабатываем каждую уникальную дату
    Object.entries(groupedByDate).forEach(([dateKey, group]) => {
      const reports = group.reports;

      // Формируем месяц и год для отчета
      const month = moment(group.date).format('MMMM');
      const year = moment(group.date).format('YYYY');
      const reportTitle = isDaily
        ? `За ${moment(group.date).format('DD.MM.YYYY')}`
        : `За ${month} ${year} р.`;

      // Сортируем отчеты: сначала обычные, потом онлайн
      const sortedReports = [...reports].sort((a, b) => {
        if (!a.isOnlineSales && b.isOnlineSales) return -1;
        if (a.isOnlineSales && !b.isOnlineSales) return 1;
        return 0;
      });

      // Создаем блоки данных по типам отчетов (общий и онлайн)
      const reportData = {
        general: null,
        online: null,
      };

      // Разделяем отчеты на общие и онлайн
      sortedReports.forEach((report) => {
        const { amountWithoutVAT, cash, vat, amountWithVAT, exciseTax, status, isOnlineSales, turnoverApproving, createdOn, modifiedOn, submitDate, confirmDate } =
          report;

        const type = isOnlineSales ? 'online' : 'general';
        reportData[type] = {
          amountWithoutVAT,
          cash,
          vat,
          amountWithVAT,
          exciseTax,
          status,
          createdOn,
          modifiedOn,
          turnoverApproving,
          submitDate,
          confirmDate,
        };
      }); // Создаем объединенную строку с данными по всем типам отчетов
      const reportRow = {
        key: `report-${dateKey}`,
        name: (
          <div className="report-value-container">
            <div className="report-header">
              <div className="report-title">{reportTitle}</div>
            </div>
            <div className="report-types">
              {reportData.general && (
                <div className="report-type-general">
                  <div className="report-type-label">Загальний обсяг продажу</div>
                </div>
              )}
              {reportData.online && (
                <div className="report-type-online">
                  <div className="report-type-label-online">Онлайн-продаж</div>
                </div>
              )}
            </div>
          </div>
        ),
        amountWithoutVAT: (
          <div className="report-value-container">
            <div className="report-types">
              {reportData.general && (
                <div className="report-type-general">
                  <div className="report-values">
                    <div className="general-value">
                      {formatNumber(reportData.general.amountWithoutVAT)} грн
                    </div>
                  </div>
                </div>
              )}
              {reportData.online && (
                <div className="report-type-online">
                  <div className="report-values">
                    <div className="online-value">
                      {formatNumber(reportData.online.amountWithoutVAT)} грн
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ),
        cash: currentContactStore.cash ? (
          <div className="report-value-container">
            <div className="report-types">
              {reportData.general && reportData.general.cash !== undefined && (
                <div className="report-type-general">
                  <div className="report-values">
                    <div className="general-value">{formatNumber(reportData.general.cash)} грн</div>
                  </div>
                </div>
              )}
              {reportData.online && reportData.online.cash !== undefined && (
                <div className="report-type-online">
                  <div className="report-values">
                    <div className="online-value">{formatNumber(reportData.online.cash)} грн</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : null,
        vat: (
          <div className="report-value-container">
            <div className="report-types">
              {reportData.general && (
                <div className="report-type-general">
                  <div className="report-values">
                    <div className="general-value">{formatNumber(reportData.general.vat)} грн</div>
                  </div>
                </div>
              )}
              {reportData.online && (
                <div className="report-type-online">
                  <div className="report-values">
                    <div className="online-value">{formatNumber(reportData.online.vat)} грн</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ),
        amountWithVAT: (
          <div className="report-value-container">
            <div className="report-types">
              {reportData.general && (
                <div className="report-type-general">
                  <div className="report-values">
                    <div className="general-value">
                      {formatNumber(reportData.general.amountWithVAT)} <span>грн</span>
                    </div>
                  </div>
                </div>
              )}
              {reportData.online && (
                <div className="report-type-online">
                  <div className="report-values">
                    <div className="online-value">
                      {formatNumber(reportData.online.amountWithVAT)} <span>грн</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ),
        exciseTax: currentContactStore.excise ? (
          <div className="report-value-container">
            <div className="report-types">
              {reportData.general && reportData.general.exciseTax !== undefined && (
                <div className="report-type-general">
                  <div className="report-values">
                    <div className="general-value">
                      {formatNumber(reportData.general.exciseTax)} <span>грн</span>
                    </div>
                  </div>
                </div>
              )}
              {reportData.online && reportData.online.exciseTax !== undefined && (
                <div className="report-type-online">
                  <div className="report-values">
                    <div className="online-value">
                      {formatNumber(reportData.online.exciseTax)} <span>грн</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        ) : null,
        status: (() => {
          const mainItem = reportData.general || reportData.online;
          if (!mainItem) return null;

          const isApproved = mainItem.turnoverApproving === true || mainItem.turnoverApproving === 'true';
          const mainStatus = isApproved ? 'Затверджено' : mainItem.status;
          const statusUpper = (mainStatus || '').toUpperCase();
          const isSubmitted = statusUpper === 'ПОДАНО' || statusUpper === 'SUBMITTED';
          const isApprovedStatus = statusUpper === 'ЗАТВЕРДЖЕНО' || statusUpper === 'APPROVED' || statusUpper === 'ПЕРЕВІРЕНО';

          // Пріоритет: спеціалізовані поля > fallback на createdOn/modifiedOn
          let displayDate = null;
          if (isApprovedStatus && mainItem.confirmDate) {
            displayDate = moment(mainItem.confirmDate).format('DD.MM.YYYY HH:mm');
          } else if (isSubmitted && mainItem.submitDate) {
            displayDate = moment(mainItem.submitDate).format('DD.MM.YYYY HH:mm');
          } else {
            // Fallback для старих записів без нових полів
            const createdText = mainItem.createdOn ? moment(mainItem.createdOn).format('DD.MM.YYYY HH:mm') : null;
            const modifiedText = mainItem.modifiedOn ? moment(mainItem.modifiedOn).format('DD.MM.YYYY HH:mm') : null;
            displayDate = isSubmitted ? createdText : (modifiedText || createdText);
          }

          return (
            <div className="report-value-container" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', padding: '4px 0' }}>
              {getStatusTag(mainStatus)}
              {displayDate && (
                <span style={{ fontSize: '11px', color: '#666', marginTop: '4px', textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {displayDate}
                </span>
              )}
            </div>
          );
        })(),
      };

      result.push(reportRow);
    });

    // Сортируем результат по дате (от новых к старым)
    result.sort((a, b) => {
      const dateKeyA = a.key.split('-')[1]; // Получаем дату из ключа
      const dateKeyB = b.key.split('-')[1];

      const dateA = moment(groupedByDate[dateKeyA]?.date);
      const dateB = moment(groupedByDate[dateKeyB]?.date);
      return dateB - dateA; // Сортировка от новых к старым
    });

    return result;
  }, [data, isDaily, currentContactStore]);
  return (
    <Table
      className="default-table request-table"
      columns={columns}
      dataSource={prepareTableData}
      locale={{ emptyText: 'Ще немає даних для відображення' }}
      pagination={false}
    />
  );
};

export default CommodityTable;
